import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@betareader/design-system';
import styled from 'styled-components';
import { CritiqueCheckpoint } from 'src/hooks/useCritiqueGroups';
import {
  CheckpointState,
  daysUntil,
  getCheckpointState,
  isRequired,
  isSelfMarkable,
  sortCheckpoints,
} from 'src/utils/critiqueGroupUtil';

/**
 * The shared checkpoint timeline (design D1 / H3) — "a shared journey", used by
 * both roles. The host passes `renderMeta` to show the who's-done pulse; a
 * member passes their own per-checkpoint status. Ordering and state logic are
 * identical for both so the group genuinely sees the same journey.
 */

const Wrap = styled.div`
  position: relative;
  padding: 6px 2px;
`;

// The spine is decorative; it's inset to pass behind the state markers.
const Spine = styled.div`
  position: absolute;
  left: 20px;
  top: 30px;
  bottom: 30px;
  width: 2px;
  background: #ece7db;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 18px;
  position: relative;
  &:last-child {
    margin-bottom: 0;
  }
`;

const Marker = styled.div<{ $state: CheckpointState }>`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  flex-shrink: 0;
  background: ${({ $state }) => ($state === 'completed' ? '#2fa84f' : '#fff')};
  color: ${({ $state }) => {
    switch ($state) {
      case 'completed':
        return '#fff';
      case 'due_today':
        return '#ef7d22';
      case 'due_soon':
        return '#c88a1a';
      case 'overdue':
        return '#d64545';
      default:
        return '#b7b2a6';
    }
  }};
  border: ${({ $state }) => {
    switch ($state) {
      case 'completed':
        return 'none';
      case 'due_today':
        return '2px solid #ef7d22';
      case 'due_soon':
        return '2px solid #e6b23c';
      case 'overdue':
        return '2px solid #d64545';
      default:
        return '2px dashed #d0cabb';
    }
  }};
  ${({ $state }) =>
    $state === 'due_today' ? 'box-shadow: 0 0 0 5px #fdeede;' : ''}
`;

const Card = styled.div<{ $state: CheckpointState }>`
  flex: 1;
  /* Without this the card can't shrink past its contents, and the marker beside
     it never yields (flex-shrink: 0), so a long label widens the whole page. */
  min-width: 0;
  border-radius: 12px;
  padding: 13px 15px;
  background: ${({ $state }) =>
    $state === 'completed' ? '#f4faf5' : $state === 'upcoming' ? '#fbfaf6' : '#fff'};
  border: ${({ $state }) => {
    switch ($state) {
      case 'completed':
        return '1px solid #dcefe0';
      case 'due_today':
        return '2px solid #f6c893';
      case 'overdue':
        return '1px solid #f0d4d4';
      case 'upcoming':
        return '1px dashed #e0dccf';
      default:
        return '1px solid #e7e3d9';
    }
  }};
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Label = styled.span<{ $muted?: boolean }>`
  font-weight: 800;
  font-size: 14.5px;
  /* Host-authored free text — a long unbroken label must not set the page's
     min-content width. word-break is the Safari < 15.4 stand-in for the
     standard value (see components/critiqueGroups/memberName.tsx). */
  word-break: break-word;
  overflow-wrap: anywhere;
  color: ${({ $muted }) => ($muted ? '#6b675e' : '#2c2a26')};
`;

const Pill = styled.span<{ $bg: string; $fg: string; $border?: string }>`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  ${({ $border }) => ($border ? `border: 1px solid ${$border};` : '')}
  white-space: nowrap;
`;

const Sub = styled.div`
  font-size: 12px;
  color: #8a857b;
  margin-top: 3px;
`;

const markerSymbol = (state: CheckpointState): string => {
  switch (state) {
    case 'completed':
      return '✓';
    case 'due_today':
      return '⚑';
    case 'due_soon':
      return '⌛';
    case 'overdue':
      return '!';
    default:
      return '⚑';
  }
};

/** Human date — locale-aware, no time (checkpoints are day-grained). */
export const formatCheckpointDate = (dueAt: string, locale?: string): string =>
  new Date(dueAt).toLocaleDateString(locale || undefined, {
    month: 'short',
    day: 'numeric',
  });

export const useCheckpointStatePill = () => {
  const { t } = useTranslation();
  return (checkpoint: CritiqueCheckpoint, state: CheckpointState) => {
    const days = daysUntil(checkpoint.dueAt);
    switch (state) {
      case 'completed':
        return { text: t('CritiqueCpCompleted', 'Completed'), bg: '#e2f4e6', fg: '#2fa84f' };
      case 'due_today':
        return { text: t('CritiqueCpDueToday', 'Due today'), bg: '#fdeede', fg: '#c85f14' };
      case 'due_soon':
        return {
          text: t('CritiqueCpDueInDays', 'Due in {{count}} days', { count: days }),
          bg: '#fbf1d8',
          fg: '#c88a1a',
        };
      case 'overdue':
        return { text: t('CritiqueCpOverdue', 'Overdue'), bg: '#fbeaea', fg: '#d64545' };
      default:
        return { text: t('CritiqueCpUpcoming', 'Upcoming'), bg: '#f0ece3', fg: '#8a857b' };
    }
  };
};

/** Plain-language description of what a checkpoint asks for. */
export const useDeliverableLabel = () => {
  const { t } = useTranslation();
  return (checkpoint: CritiqueCheckpoint): string => {
    const d = checkpoint.deliverable;
    if (!d) {
      return checkpoint.part
        ? t('CritiqueDelivReachPart', 'Read to here')
        : t('CritiqueDelivMilestone', 'A shared milestone');
    }
    switch (d.type) {
      case 'reached_part':
        return t('CritiqueDelivReachPart', 'Read to here');
      case 'comment':
        return t('CritiqueDelivComment', 'Leave a comment');
      case 'survey':
        return t('CritiqueDelivSurvey', 'Answer the chapter survey');
      case 'custom':
        return d.description || t('CritiqueDelivCustom', 'Custom — self-marked');
      default:
        return '';
    }
  };
};

export type CheckpointRowMeta = {
  /** Whether THIS viewer (or the group) counts the checkpoint as done. */
  done?: boolean;
  /** Extra content under the subtitle — the who's-done pulse, or an action. */
  footer?: React.ReactNode;
  /** Right-aligned action (e.g. "Mark done", "Edit"). */
  action?: React.ReactNode;
};

type TimelineProps = {
  checkpoints: CritiqueCheckpoint[];
  /** Per-checkpoint viewer state; keyed by checkpoint _id. */
  meta?: Record<string, CheckpointRowMeta>;
  now?: Date;
  emptyState?: React.ReactNode;
};

export const CheckpointTimeline: React.FC<TimelineProps> = ({
  checkpoints,
  meta = {},
  now,
  emptyState,
}) => {
  const { t } = useTranslation();
  const statePill = useCheckpointStatePill();
  const deliverableLabel = useDeliverableLabel();
  const sorted = sortCheckpoints(checkpoints);

  if (sorted.length === 0) return <>{emptyState}</>;

  return (
    <Wrap data-testid="checkpoint-timeline">
      <Spine />
      {sorted.map(cp => {
        const rowMeta = meta[cp._id] || {};
        const state = getCheckpointState(cp, { done: rowMeta.done, now });
        const pill = statePill(cp, state);
        const guiding = !isRequired(cp);
        return (
          <Row key={cp._id} data-testid={`checkpoint-row-${cp._id}`}>
            <Marker $state={state} data-testid={`checkpoint-marker-${state}`}>
              <span aria-hidden="true">{markerSymbol(state)}</span>
            </Marker>
            <Card $state={state}>
              <Head>
                <Label $muted={state === 'upcoming'}>{cp.label}</Label>
                <Pill $bg={pill.bg} $fg={pill.fg}>
                  {pill.text}
                </Pill>
                {guiding ? (
                  <Pill $bg="#fff" $fg="#8a857b" $border="#e0dccf">
                    {t('CritiqueCpGuiding', 'Guiding')}
                  </Pill>
                ) : (
                  <Pill $bg="#e3f6f4" $fg="#00817b">
                    {t('CritiqueCpRequired', 'Required')}
                  </Pill>
                )}
                {isSelfMarkable(cp) && (
                  <Pill $bg="#fff" $fg="#8a857b" $border="#e0dccf">
                    {t('CritiqueCpSelfMarked', 'Custom · self-marked')}
                  </Pill>
                )}
                {rowMeta.action && (
                  <span style={{ marginLeft: 'auto' }}>{rowMeta.action}</span>
                )}
              </Head>
              <Sub>
                {formatCheckpointDate(cp.dueAt)} · {deliverableLabel(cp)}
              </Sub>
              {rowMeta.footer}
            </Card>
          </Row>
        );
      })}
    </Wrap>
  );
};

/** The calm progress ring + counts of the who's-done pulse (design D1 / H4). */
export const DonePulse: React.FC<{ done: number; total: number }> = ({
  done,
  total,
}) => {
  const { t } = useTranslation();
  const pct = total > 0 ? done / total : 0;
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 11 }}
      data-testid="done-pulse"
    >
      <div
        style={{
          position: 'relative',
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: `conic-gradient(#00b5ad ${pct * 360}deg, #eceae2 ${pct * 360}deg)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--br-surface)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 10,
            color: 'var(--br-accent)',
          }}
        >
          {done}/{total}
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--br-muted)' }}>
        {t('CritiqueNDone', '{{done}} of {{total}} done', { done, total })}
      </div>
    </div>
  );
};

export const MarkDoneButton: React.FC<{
  onClick: () => void;
  loading?: boolean;
  done?: boolean;
}> = ({ onClick, loading, done }) => {
  const { t } = useTranslation();
  if (done) {
    return (
      <span style={{ fontSize: 12, color: 'var(--br-success-text)', fontWeight: 700 }}>
        <span aria-hidden="true">✓ </span>
        {t('CritiqueMarkedDone', 'Marked done')}
      </span>
    );
  }
  return (
    <Button
      variant="secondary"
      size="small"
      onClick={onClick}
      disabled={loading}
      style={{ borderRadius: 7 }}
      data-testid="mark-done-button"
    >
      {t('CritiqueMarkDone', 'Mark done')}
    </Button>
  );
};
